import React, { useContext, useRef, useState, useEffect, useCallback } from "react";
import WaveSurfer from "wavesurfer.js";
import SidebarContext from "../../contexts/MyContext";
import AudioPlayer from "react-h5-audio-player";
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { truncateFileName } from "../../utils/truncateFileName";
import { useNavigate } from "react-router-dom";
import { AudioHandler } from "../../Apihander/AudioRecording/AudioRecording"
import { errorToast, successToast } from "../../utils/notificationUtils";
import { formatTimeWithOptionalHours, timeStringToSeconds } from "../../utils/timeFile";
import "../Waveform/WaveForm.css";
import "../../css/app/aiResults.css";
import { Icon } from "@iconify/react";
import Loader from "../../animations/Loader";
import { useTrackNavigation } from "../../utils/trackNavigationUtils";
import { destroyWavesurfer, loadWavesurfer } from "../../utils/wavesurferUtils";
import { AUTO_DETECT, TRANSLATE_LANGUAGES, getLanguageLabel, translateText } from "../../utils/translate";

// Lets role="button" images respond to Enter/Space like real buttons.
const activateOnKey = (e) => { if (e.key === "Enter" || e.key === " ") e.currentTarget.click(); };

const TranscriptRecordingLogic = () => {
    const navigate = useNavigate()
    const { isPrevDisabled, isNextDisabled, playPreviousTrack, playNextTrack, } = useTrackNavigation()

    const { selectedTranscriptAudio, setTabIndex, textAreaContent, setTextAreaContent, isUpdateTranscribe, setIsUpdateTranscribe, setActionTabIndex, setAudio, audioIndex, setAudioIndex, listTabIndex, filterAudioData, favoriteAudio, setSelectedTranscriptAudio, setFavoriteAudio, setFilterAudioData } = useContext(SidebarContext)
    const textAreaRef = useRef(null);
    const { transcript, resetTranscript, listening, browserSupportsSpeechRecognition } = useSpeechRecognition();
    const [previousContent, setPreviousContent] = useState('');
    const [summaryContent, setSummaryContent] = useState('');
    const [previousSummary, setPreviousSummary] = useState('');
    const [timeFormat, setTimeFormat] = useState("mm:ss");
    const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    // const [duration, setDuration] = useState(selectedTranscriptAudio?.audio?.timeDuration || 0);
    const [duration, setDuration] = useState(0);

    const [isLoading, setIsLoading] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);

    const wavesurfer = useRef(null);
    const seekbarRef = useRef(null);
    const animationRef = useRef(null);
    const activeAudioIdRef = useRef(null);
    const translationRef = useRef(null);

    // Transcript and summary come out of one job, so they are the two tabs on this screen.
    const [activeTab, setActiveTab] = useState("transcript");
    const [targetLanguage, setTargetLanguage] = useState("en");
    const [translateStatus, setTranslateStatus] = useState('');
    const [isTranslating, setIsTranslating] = useState(false);
    // Keyed by tab, so a translation survives switching between transcript and summary.
    const [translations, setTranslations] = useState({});

    // One pane per tab, so the editor and its buttons work the same on both.
    const panes = {
        transcript: {
            content: textAreaContent,
            setContent: setTextAreaContent,
            previous: previousContent,
            setPrevious: setPreviousContent,
            title: "Transcribed Text",
            placeholder: "Transcript Recording",
            fileName: "transcript.txt",
        },
        summary: {
            content: summaryContent,
            setContent: setSummaryContent,
            previous: previousSummary,
            setPrevious: setPreviousSummary,
            title: "Summary",
            placeholder: "No summary was saved with this recording.",
            fileName: "summary.txt",
        },
    };
    const pane = panes[activeTab] || panes.transcript;
    const paneContent = pane.content;
    const panePrevious = pane.previous;
    // The translation always belongs to whichever tab is open.
    const translation = translations[activeTab];


    // Reload the summary buffer whenever a different recording is selected
    useEffect(() => {
        setSummaryContent(selectedTranscriptAudio?.transcribe?.summaryText || '');
        setPreviousSummary('');
    }, [selectedTranscriptAudio?._id, selectedTranscriptAudio?.transcribe?.summaryText]);

    // The translation belongs to one recording, so the saved one is restored when it opens
    useEffect(() => {
        const saved = selectedTranscriptAudio?.translations || [];
        const restored = saved.find((item) => item.languageCode === targetLanguage) || saved[saved.length - 1];
        setTranslations(restored ? { transcript: { text: restored.translatedText, language: restored.languageCode, providerLabel: 'saved translation' } } : {});
        if (restored) setTargetLanguage(restored.languageCode);
        setTranslateStatus('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTranscriptAudio?._id]);

    // Update state when transcript changes during real-time transcription
    useEffect(() => {
        if (!isLoadingTranscript && activeTab === "transcript") {
            setTextAreaContent(transcript);

        }


    }, [transcript]);


    // useEffect(() => {
    //     if (selectedTranscriptAudio) {
    //         listTabIndex === 1 ? (
    //             setSelectedTranscriptAudio(filterAudioData[audioIndex])
    //         ) : (
    //             setSelectedTranscriptAudio(favoriteAudio[audioIndex])
    //         )

    //     }
    // }, [audioIndex, filterAudioData, favoriteAudio, listTabIndex]);

    useEffect(() => {
        let currentAudio;

        if (listTabIndex === 1) {
            currentAudio = filterAudioData[audioIndex];
        } else {
            currentAudio = favoriteAudio[audioIndex];
        }

        if (currentAudio) {
            setSelectedTranscriptAudio(currentAudio);

            // Only a different recording resets the tab; a list refresh after a save keeps it.
            if (activeAudioIdRef.current !== currentAudio._id) {
                activeAudioIdRef.current = currentAudio._id;
                setActiveTab("transcript");
            }

            doHandleVerifyTranscribeAudioData(currentAudio);
        }
    }, [audioIndex, filterAudioData, favoriteAudio, listTabIndex]);

    // Fetch existing transcript when audio is selected
    useEffect(() => {
        if (selectedTranscriptAudio?.audio?.url) {
            // setTextAreaContent('');
            resetTranscript();
            const timer = setTimeout(() => {
            }, 300); // Debounce with 300ms delay

            return () => clearTimeout(timer);
        }
        if (selectedTranscriptAudio?.audio?.timeDuration >= 3600) {
            // 3600 seconds = 1 hour
            setTimeFormat("auto");
        } else {
            setTimeFormat("mm:ss");
        }
    }, [selectedTranscriptAudio, resetTranscript]);

    useEffect(() => {
        let timeoutId;
        const fileExtension = selectedTranscriptAudio?.audio?.fileName?.split('.').pop().toLowerCase()

        if (selectedTranscriptAudio?.audio?.url) {
            setIsLoading(true);
            timeoutId = setTimeout(() => {
                destroyWavesurfer(wavesurfer.current);
                wavesurfer.current = null;

                wavesurfer.current = WaveSurfer.create({
                    container: document.createElement('div'),
                    backend: 'WebAudio',
                    normalize: true,
                    responsive: true,
                });

                if (fileExtension === "caf" || fileExtension === "m4a") {
                    wavesurfer.current.seekTo(0);
                    seekbarRef.current.value = 0
                    seekbarRef.current.style.setProperty('--seek-before-width', `${0}%`);
                    seekbarRef.current.style.setProperty('background', `linear-gradient(
    to right,
    #334155 0%,
    #334155 0%,
    #dddddd 0%,
    #dddddd 100%
  )`);

                } else {

                    loadWavesurfer(wavesurfer.current, selectedTranscriptAudio?.audio?.url);
                }



                wavesurfer.current.on('ready', () => {
                    setIsLoading(false);
                    setDuration(wavesurfer.current?.getDuration());
                    // play() rejects if the instance is torn down mid-start
                    Promise.resolve(wavesurfer.current?.play()).catch(() => { });
                    setIsPlaying(true);
                    updateSeekbar();
                });

                wavesurfer.current.on('play', () => {
                    setIsPlaying(true);
                    animationRef.current = requestAnimationFrame(updateSeekbar);
                });

                wavesurfer.current.on('pause', () => {
                    setIsPlaying(false);
                    cancelAnimationFrame(animationRef.current);
                });

                wavesurfer.current.on('finish', () => {
                    setIsPlaying(false);
                    cancelAnimationFrame(animationRef.current);
                    // Ensure the progress bar reaches 100% at the end of the audio
                    if (seekbarRef.current) {
                        seekbarRef.current.value = 100;
                        seekbarRef.current.style.setProperty('--seek-before-width', `100%`);
                    }
                });

            }, 300); // 300ms delay to avoid fast switching issues

            return () => {
                clearTimeout(timeoutId);
                cancelAnimationFrame(animationRef.current);
                destroyWavesurfer(wavesurfer.current);
                wavesurfer.current = null; // Ensure it's reset after destroy
            };
        }
    }, [selectedTranscriptAudio?.audio?.url]);

    const updateSeekbar = useCallback(() => {
        if (wavesurfer.current && seekbarRef.current) {
            const currentTime = wavesurfer.current.getCurrentTime();
            const duration = wavesurfer.current.getDuration();

            setCurrentTime(currentTime);
            setDuration(duration);

            let progress = 0;
            if (duration > 0) {
                progress = (currentTime / duration) * 100;
            }

            seekbarRef.current.value = progress;
            seekbarRef.current.style.setProperty('--seek-before-width', `${progress}%`);

            animationRef.current = requestAnimationFrame(updateSeekbar);

        }
    }, []);

    const handleSeekbarChange = (e) => {
        const seekTo = parseFloat(e.target.value) / 100;
        if (wavesurfer.current) {
            wavesurfer.current.seekTo(seekTo);
        }
    };

    const playPause = () => {
        if (wavesurfer.current && !isLoading) {
            wavesurfer.current.playPause();
        }
    };

    const skipBackward = () => {
        if (wavesurfer) {
            const totalDuration = wavesurfer.current.getDuration();
            const newTime = Math.max(currentTime - 5, 0);

            wavesurfer.current.seekTo(newTime / totalDuration);
        }
    }

    const skipForward = () => {
        if (wavesurfer) {
            const totalDuration = wavesurfer.current.getDuration();
            const newTime = Math.min(currentTime + 5, totalDuration);
            wavesurfer.current.seekTo(newTime / totalDuration);
        }
    };

    // const isPrevDisabled = audioIndex <= 0;
    // const isNextDisabled = (listTabIndex == 1 && audioIndex == filterAudioData.length - 1) || (listTabIndex == 2 && audioIndex == favoriteAudio.length - 1);


    // Play next track based on whether the current audio is folder audio or not
    // const playNextTrack = () => {
    //     if (listTabIndex == 1 ? filterAudioData?.length : favoriteAudio?.length - 1) {
    //         setAudioIndex(prevIndex => prevIndex + 1);
    //     }

    // };

    // Play next track, skipping unsupported formats



    // Play previous track based on whether the current audio is folder audio or not
    // const playPreviousTrack = () => {
    //     if (audioIndex > 0) {
    //         setAudioIndex(prevIndex => prevIndex - 1);

    //     }
    // };




    const handleAudioEnd = () => {
        SpeechRecognition.stopListening();
        // console.log(textAreaContent, 'textAreaContent')
    };

    // Handle text area changes
    const handleTextAreaChange = (e) => {
        pane.setContent(e.target.value);
    };


    // Save the transcript to a text file
    const handleSaveTextFile = () => {
        const blob = new Blob([paneContent], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = pane.fileName;
        a.click();
        URL.revokeObjectURL(url);
    };


    const handleSaveOrUpdateTranscript = async () => {

        const idValue = selectedTranscriptAudio?._id
        // Both fields go up every time so saving one pane never blanks the other.
        const payload = {
            "transcribeText": textAreaContent,
            "summaryText": summaryContent,
            "audioId": idValue
        }
        try {
            let response;
            response = await AudioHandler.doHandalUpdateTranscriptAudio(payload);
            const updatedAudio = {
                ...selectedTranscriptAudio,
                transcribe: {
                    ...selectedTranscriptAudio.transcribe,
                    transcribeText: textAreaContent, // use the updated text from textarea
                    summaryText: summaryContent
                }
            };
            setTextAreaContent(response?.data?.transcribe?.transcribeText ?? textAreaContent)
            setSummaryContent(response?.data?.transcribe?.summaryText ?? summaryContent)
            setPreviousContent('')
            setPreviousSummary('')

            // console.log(response, "responseeee");

            // Update filterAudioData state
            setFilterAudioData((prevData) =>
                prevData.map((item) => (item._id === idValue ? updatedAudio : item))
            );

            // Update favoriteAudio state
            setFavoriteAudio((prevFavorites) =>
                prevFavorites.map((item) => (item._id === idValue ? updatedAudio : item))
            );

            // Update selectedTranscriptAudio state
            setSelectedTranscriptAudio(updatedAudio);

            // Update setAudio state if it's the same audio object
            setAudio((prevAudio) => (prevAudio?._id === idValue ? updatedAudio : prevAudio));

            successToast(response?.message)
        } catch (error) {
            console.error('Error saving transcript audio:', error);
        }
    };


    // Translates whichever tab is open, through the provider chain in utils/translate.
    const handleTranslate = async () => {
        const sourceText = paneContent || '';
        const translatedTab = activeTab;
        const audioId = selectedTranscriptAudio?._id;

        if (isTranslating) return;
        if (!sourceText.trim()) {
            errorToast(`There is no ${translatedTab} to translate yet`);
            return;
        }

        setIsTranslating(true);
        setTranslateStatus('Translating...');

        try {
            const result = await translateText({
                text: sourceText,
                sourceLanguage: AUTO_DETECT,
                targetLanguage,
                onStatus: setTranslateStatus,
            });

            // Stored against the tab it came from, so switching tabs mid-run cannot misfile it.
            setTranslations((prevTranslations) => ({
                ...prevTranslations,
                [translatedTab]: {
                    text: result.text,
                    language: targetLanguage,
                    providerLabel: result.providerLabel,
                },
            }));

            // Only the transcript is stored, since the API keeps one translation per language.
            if (audioId && translatedTab === 'transcript') {
                const saved = await AudioHandler.doSaveTranslation(audioId, { languageCode: targetLanguage, translatedText: result.text });
                if (saved?.statusCode !== 200) {
                    errorToast(saved?.response?.data?.message || "We couldn't save this translation. Please try again.");
                } else {
                    // Kept on the loaded lists so reopening the recording shows it without a refetch.
                    const withTranslations = (item) => (item._id === audioId ? { ...item, translations: saved.data.translations } : item);
                    setFilterAudioData((prevData) => prevData.map(withTranslations));
                    setFavoriteAudio((prevFavorites) => prevFavorites.map(withTranslations));
                    setSelectedTranscriptAudio((prevAudio) => (prevAudio?._id === audioId ? withTranslations(prevAudio) : prevAudio));
                }
            }
        } catch (error) {
            errorToast(error?.message || "We couldn't translate this text right now. Please try again.");
        } finally {
            setIsTranslating(false);
            setTranslateStatus('');
        }
    };

    const handleSaveTranslationFile = () => {
        const blob = new Blob([translation?.text || ''], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${activeTab}-${translation?.language || targetLanguage}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const copyToClipboard = () => {
        if (textAreaRef.current) {
            textAreaRef.current.select();
            document.execCommand('copy');
        }
    };

    const copyTranslationToClipboard = () => {
        if (translationRef.current) {
            translationRef.current.select();
            document.execCommand('copy');
        }
    };




    const handleResetText = () => {
        pane.setContent(pane.previous);
        pane.setPrevious('');
        // Only the transcript pane is fed by live speech recognition.
        if (activeTab === "transcript") resetTranscript();
    };

    const handleEditText = () => {
        if (!pane.previous) {
            pane.setPrevious(pane.content);
        }

        if (textAreaRef.current) {
            textAreaRef.current.focus();
            textAreaRef.current.selectionStart = paneContent.length;
            textAreaRef.current.selectionEnd = paneContent.length;
        }
    }

    const doHandleVerifyTranscribeAudioData = (el) => {
        const checkTranscribeStatus = el?.transcribe?.isTranscribe

        if (checkTranscribeStatus == false || checkTranscribeStatus == undefined) {
            setActionTabIndex(null);
            setAudio(el)

        } else {
            setTextAreaContent(el?.transcribe?.transcribeText)
        }
    };


    useEffect(() => {
        if (textAreaContent) {
            setTextAreaContent(textAreaContent)
        }
    }, [textAreaContent])
    return (
        <>
            <div className={`vr-air ${activeTab === "summary" ? "vr-tone-teal" : "vr-tone-indigo"}`}>
                {
                    selectedTranscriptAudio.length == 0 ?
                        <div className="vr-air-empty">
                            <img src='/img/transcript/transcript_img.png' />
                            <h3 className="vr-air-empty-title">
                                Select Recording for Transcript & Summary
                            </h3>
                            <p className="vr-air-empty-text">
                                Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's
                            </p>
                        </div>
                        :
                        (
                            <>
                                <header className="vr-air-head">
                                    <div className="vr-icon-tile is-solid vr-air-head-tile">
                                        <Icon icon={activeTab === "summary" ? "lucide:list-checks" : "lucide:file-text"} width="20" height="20" />
                                    </div>
                                    <div className="vr-air-head-text">
                                        <h6 className="vr-air-title">
                                            Transcript & Summary
                                        </h6>
                                        <div className="vr-air-meta">
                                            <Icon icon="lucide:audio-lines" width="15" height="15" className="vr-air-meta-icon" />
                                            <h6 className="vr-air-file">
                                                {truncateFileName(selectedTranscriptAudio?.audio?.fileName, 35)}
                                            </h6>
                                            {duration > 0 && <span className="vr-air-chip">{formatTimeWithOptionalHours(duration)}</span>}
                                        </div>
                                    </div>
                                    <img src="./img/cancelIcon.png" height={24} width={24} onClick={(e) => setActionTabIndex(null)} className="vr-air-close" alt="Close" role="button" tabIndex={0} onKeyDown={activateOnKey} />
                                </header>

                                <section className="vr-air-player">
                                    <div className="vr-air-transport">
                                        <img
                                            src={isLoading || isPrevDisabled ? "/img/dashboard/disabled_previous.png" : "/img/dashboard/previous.png"}
                                            width="36px"
                                            height="36px"
                                            className={`vr-air-nav${isLoading || isPrevDisabled ? " is-disabled" : ""}`}
                                            onClick={!isLoading && !isPrevDisabled ? playPreviousTrack : undefined}
                                            alt="prev"
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={activateOnKey}
                                        />

                                        <button onClick={skipBackward} className="vr-air-skip" title="Back 5 seconds">
                                            <img src="/img/trim/backward.png" alt="Backward" />
                                        </button>

                                        <div
                                            className={`vr-air-play${isPlaying ? " is-playing" : ""}`}
                                            onClick={playPause}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={activateOnKey}
                                            aria-label={isPlaying ? "Pause" : "Play"}
                                        >
                                            {
                                                isLoading ?
                                                    <>
                                                        <Loader />
                                                    </>
                                                    :
                                                    <>
                                                        {
                                                            isPlaying ?
                                                                <>
                                                                    <img
                                                                        src="/img/dashboard/pause.png"
                                                                        alt="Pause Button"
                                                                        className="vr-air-play-icon"
                                                                    />
                                                                </>
                                                                :
                                                                <>
                                                                    <img
                                                                        src="/img/dashboard/play.png"
                                                                        alt="Play Button"
                                                                        className="vr-air-play-icon is-play"
                                                                    />
                                                                </>
                                                        }
                                                    </>
                                            }


                                        </div>

                                        <button onClick={skipForward} className="vr-air-skip" title="Forward 5 seconds">
                                            <img src="/img/trim/forward.png" alt="Forward" />
                                        </button>

                                        <img
                                            src={isLoading || isNextDisabled ? "/img/dashboard/disabled_next.png" : "/img/dashboard/next.png"}
                                            width="36px"
                                            height="36px"
                                            className={`vr-air-nav${isLoading || isNextDisabled ? " is-disabled" : ""}`}
                                            onClick={!isLoading && !isNextDisabled ? playNextTrack : undefined}
                                            alt="next"
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={activateOnKey}
                                        />

                                    </div>
                                    <div className="vr-air-seek">
                                        <span className="vr-air-time">{formatTimeWithOptionalHours(currentTime)}</span>
                                        <input
                                            ref={seekbarRef}
                                            type="range"
                                            min="0"
                                            max="100"
                                            defaultValue="0"
                                            onChange={handleSeekbarChange}
                                            style={{
                                                background: `linear-gradient(to right, var(--tone) 0%, var(--tone) ${(currentTime / duration) * 100}%, var(--vr-border) ${(currentTime / duration) * 100}%, var(--vr-border) 100%)`,
                                            }}
                                            className="seekbar vr-air-seekbar"
                                            aria-label="Seek"
                                        />
                                        <span className="vr-air-time is-end">{formatTimeWithOptionalHours(duration)}</span>
                                    </div>
                                </section>

                                <section className="vr-air-doc">
                                    <div className="vr-air-doc-head">
                                        <div className={`vr-air-tabs${activeTab === 'summary' ? ' is-second' : ''}`} role="tablist">
                                            {[{ key: 'transcript', label: 'Transcript' }, { key: 'summary', label: 'Summary' }].map((tab) => (
                                                <button
                                                    key={tab.key}
                                                    onClick={() => setActiveTab(tab.key)}
                                                    className={`vr-air-tab${activeTab === tab.key ? ' is-active' : ''}`}
                                                    role="tab"
                                                    aria-selected={activeTab === tab.key}
                                                >
                                                    {tab.label}
                                                </button>
                                            ))}
                                        </div>

                                        <div className="vr-air-translate vr-tone-sky">
                                            <span className="vr-air-translate-label">Translate</span>
                                            <select
                                                aria-label="Translate into"
                                                className="vr-air-select"
                                                value={targetLanguage}
                                                onChange={(e) => setTargetLanguage(e.target.value)}
                                                disabled={isTranslating}
                                            >
                                                {TRANSLATE_LANGUAGES.map((language) => (
                                                    <option key={language.code} value={language.code}>{language.label}</option>
                                                ))}
                                            </select>
                                            <button
                                                className="vr-btn vr-btn-navy vr-air-translate-btn"
                                                onClick={handleTranslate}
                                                disabled={isTranslating || !paneContent}
                                            >
                                                {isTranslating ? (
                                                    <span className="vr-spinner vr-air-spinner-light" role="status" />
                                                ) : (
                                                    <Icon icon="lucide:languages" width="16" height="16" />
                                                )}
                                                {isTranslating ? 'Translating' : 'Translate'}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="vr-air-doc-sub">
                                        <h1 className="vr-air-pane-title">{pane.title}</h1>
                                        {(translateStatus || translation?.text) && (
                                            <p className="vr-air-status">
                                                {translateStatus || `Translated with ${translation?.providerLabel}`}
                                            </p>
                                        )}
                                    </div>
                                    {isTranslating && <div className="vr-progress is-indeterminate vr-air-progress"><i /></div>}

                                    {/* The text and its translation sit side by side so they can be read against each other. */}
                                    <div className={`vr-air-panes is-${activeTab}`}>
                                        <div className="vr-air-pane">
                                            <textarea
                                                id="message"
                                                name="message"
                                                placeholder={pane.placeholder}
                                                className="form-control input-field scroll vr-air-text"

                                                ref={textAreaRef}
                                                value={paneContent}
                                                onChange={handleTextAreaChange}
                                                onClick={() => {
                                                    if (!pane.previous) pane.setPrevious(pane.content);
                                                }}
                                            />
                                            <div className="vr-air-pane-tools">
                                                <img
                                                    src="/img/transcript/copy.png"
                                                    alt="Copy"
                                                    title="Copy"
                                                    onClick={copyToClipboard}
                                                    className="vr-air-pane-icon"
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={activateOnKey}
                                                />
                                            </div>
                                        </div>

                                        <div className="vr-air-pane is-translation vr-tone-sky">
                                            <textarea
                                                readOnly
                                                aria-label={`${pane.title} in ${getLanguageLabel(translation?.language || targetLanguage)}`}
                                                placeholder={`Press Translate to see this ${activeTab} in ${getLanguageLabel(targetLanguage)}.`}
                                                className="form-control input-field scroll vr-air-text"
                                                ref={translationRef}
                                                value={translation?.text || ''}
                                            />
                                            {translation?.text && (
                                                <div className="vr-air-pane-tools">
                                                    <img
                                                        src="/img/transcript/copy.png"
                                                        alt="Copy translation"
                                                        title="Copy translation"
                                                        onClick={copyTranslationToClipboard}
                                                        className="vr-air-pane-icon"
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={activateOnKey}
                                                    />
                                                    <img
                                                        src="/img/transcript/Save-text-file.png"
                                                        alt="Save translation"
                                                        title="Save translation"
                                                        onClick={handleSaveTranslationFile}
                                                        className="vr-air-pane-icon"
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={activateOnKey}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className='vr-air-actions'>
                                        <div className="vr-air-actions-group">
                                            <button
                                                className={`vr-btn vr-btn-ghost vr-air-reset${panePrevious?.length == paneContent?.length || panePrevious?.length == 0 ? " is-blocked" : ""}`}
                                                disabled={panePrevious ? false : true}
                                                onClick={() => handleResetText()}>
                                                <Icon icon="lucide:rotate-ccw" width="16" height="16" />
                                                <span className={(!panePrevious && !paneContent) ? 'vr-air-dim' : ''}>Reset Text</span>
                                            </button>
                                            <button className="vr-btn vr-btn-secondary" onClick={() => handleEditText()}>
                                                <Icon icon="lucide:pencil" width="16" height="16" />
                                                <span>Edit Text</span>
                                            </button>
                                            <button className="vr-btn vr-btn-secondary" onClick={() => handleSaveTextFile()}>
                                                <Icon icon="lucide:file-down" width="16" height="16" />
                                                <span>Save text file</span>
                                            </button>
                                        </div>

                                        <button
                                            className={`vr-btn vr-btn-primary vr-air-save${paneContent ? " is-ready" : ""}`}
                                            onClick={() => handleSaveOrUpdateTranscript()}
                                            disabled={paneContent ? false : true} >
                                            <Icon icon="lucide:save" width="16" height="16" />

                                            <span className={panePrevious || paneContent ? 'is-lit' : ''}>save</span>
                                        </button>
                                    </div>
                                </section>


                            </>
                        )
                }

            </div>
        </>
    )
}

export default TranscriptRecordingLogic
